import React, { useState, useEffect, useRef } from 'react';
import {
    CreditCardIcon,
    PencilIcon,
    TrashIcon,
    StarIcon,
    EllipsisVerticalIcon,
    XMarkIcon,
    EyeIcon,
    FunnelIcon,
    ChevronDownIcon,
    ArrowDownIcon,
    Bars3Icon,
    Squares2X2Icon,
    ListBulletIcon,
    ClipboardDocumentIcon,
    DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { CardDetail } from '../../types/cardDetails';
import { toast } from 'react-hot-toast';
import CardDetailsService from '../../services/cardDetails.service';
import { useVault } from '../../contexts/VaultContext';
import { useAppSelector } from '../../hooks/useRedux';
import ConfirmModal from '../common/ConfirmModal';
import { FolderIcon, UserIcon } from '@heroicons/react/24/outline';
import { useCategories } from '../../contexts/CategoryContext';

interface CardDetailsListProps {
    details: CardDetail[];
    onUpdate: () => void;
    onEdit: (detail: CardDetail, isShared: boolean, sharedKey: string) => void;
    hideFavoriteIcon?: boolean;
    isAdminView?: boolean; 
}

const renderCategories = (entryCategories: any[], categories?: any[]) => {
    if (!entryCategories || entryCategories.length === 0) return null;

    const validCategories = entryCategories.filter(entryCategory =>
        categories?.some((userCategory: any) => userCategory.id === entryCategory.id)
    );

    if (validCategories.length === 0) return null;

    return (
        <>
            {validCategories.slice(0, 2).map((category, index) => (
                <React.Fragment key={category.id}>
                    <div className={`inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs
                        ${category.is_primary
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : 'bg-gray-100 text-gray-600 border border-gray-200'
                        }`}>
                        <FolderIcon className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate max-w-20">{category.name}</span>
                    </div>
                </React.Fragment>
            ))}
            {validCategories.length > 2 && (
                <div className="inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600 border border-gray-200">
                    +{validCategories.length - 2} more
                </div>
            )}
        </>
    );
};

// Helper function to get stored view mode preference
const getStoredCardViewMode = (): 'grid' | 'list' => {
    try {
        const stored = localStorage.getItem('cardDetailsViewMode');
        if (stored === 'grid' || stored === 'list') {
            return stored;
        }
    } catch (error) {
        console.warn('Failed to read card view mode preference from localStorage:', error);
    }
    return 'grid'; // Default fallback
};

// Helper function to store view mode preference
const storeCardViewMode = (mode: 'grid' | 'list') => {
    try {
        localStorage.setItem('cardDetailsViewMode', mode);
    } catch (error) {
        console.warn('Failed to save card view mode preference to localStorage:', error);
    }
};

const CardDetailsList: React.FC<CardDetailsListProps> = ({ details, onUpdate, onEdit, hideFavoriteIcon = false, isAdminView = false }) => {
    const [hoveredCard, setHoveredCard] = useState<string | null>(null);
    const [activeCard, setActiveCard] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
    const { searchQuery } = useVault();
    const { role_id, team_id } = useAppSelector(state => state.auth);
    const { categories, refreshCategories } = useCategories();

    // View mode and filter states - Initialize with stored preference
    const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => getStoredCardViewMode());
    const [selectedCard, setSelectedCard] = useState<CardDetail | null>(null);

    // Enhanced setViewMode that persists the preference
    const handleViewModeChange = (mode: 'grid' | 'list') => {
        setViewMode(mode);
        storeCardViewMode(mode);
    };
    const [sortOption, setSortOption] = useState<'newest' | 'oldest' | 'alphabetical'>('newest');
    const [showSharedOnly, setShowSharedOnly] = useState<boolean>(false);
    const [isSortMenuOpen, setIsSortMenuOpen] = useState<boolean>(false);
    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false);
    const [tempShowSharedOnly, setTempShowSharedOnly] = useState<boolean>(false);
    const sortMenuRef = useRef<HTMLDivElement>(null);
    const filterMenuRef = useRef<HTMLDivElement>(null);

    // Confirm modal state
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [cardToDelete, setCardToDelete] = useState<string>('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [isOwner, setIsOwner] = useState(false);

    // Detect screen size changes for responsive behavior
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleDeleteClick = (id: string, is_owner: boolean) => {
        setCardToDelete(id);
        setShowConfirmModal(true);
        setIsOwner(is_owner);
    };

    const handleConfirmDelete = async () => {
        try {
            setIsDeleting(true);
            await CardDetailsService.deleteCardDetail(cardToDelete, !isOwner);
            toast.success('Card deleted successfully');
            onUpdate();
            setActiveCard(null);
        } catch (error) {
            toast.error('Failed to delete card');
        } finally {
            setIsDeleting(false);
            setShowConfirmModal(false);
        }
    };

    const handleEdit = async (entryId: string, isShared?: boolean, sharedKey?: string) => {
        if (isAdminView) {
            // Admin view: Use already-decrypted details from props
            const detail = details.find((d) => d.id === entryId);
            if (!detail) {
                toast.error('Card not found');
                return;
            }
            // Force read-only mode for admin
            onEdit({ ...detail, permission_level: 'read' }, isShared ?? false, detail.shared_key ?? sharedKey ?? '');
            setActiveCard(null);
            return;
        }
        try {
            const detail = await CardDetailsService.getCardDetailById(entryId, isShared, sharedKey);
            if (!detail.is_owner) {
                const CardDetails = details.filter((entryId) => entryId.id === detail.id);
                detail.is_shared = CardDetails[0].is_shared;
                detail.shared_key = CardDetails[0].shared_key;
                detail.permission_level = CardDetails[0].permission_level;
            }
            onEdit(detail, isShared ?? false, detail.shared_key ?? sharedKey ?? '');
            setActiveCard(null);
        } catch (err) {
            console.error('Failed to fetch entry:', err);
            toast.error('Failed to fetch entry');
        }
    };

    const handleClone = async (detail: CardDetail) => {
        try {
            if (!isAdminView) {
                toast.error('Clone feature is only available in admin view');
                return;
            }

            if (!window.confirm(`Clone this card to your own vault?\n\nCard: ${detail.name}\nType: ${detail.card_type}`)) {
                return;
            }

            const newCard = {
                name: detail.name || undefined,
                card_type: detail.card_type || undefined,
                encrypted_card_holder_name: detail.encrypted_card_holder_name || '',
                encrypted_card_number: detail.encrypted_card_number || '',
                encrypted_expiration_date: detail.encrypted_expiration_date || '',
                encrypted_cvv: detail.encrypted_cvv || '',
                encrypted_pin: detail.encrypted_pin || undefined,
                encrypted_postal_code: detail.encrypted_postal_code || undefined,
                encrypted_notes: detail.encrypted_notes || undefined,
                category_id: detail.category_id || undefined,
                favourite: false,
                workspace_id: team_id || undefined

            };

            await CardDetailsService.createCardDetail(newCard);
            toast.success('Card cloned to your vault successfully');
            
            if (onUpdate) {
                onUpdate();
            }
        } catch (err) {
            console.error('Failed to clone card:', err);
            toast.error('Failed to clone card');
        }
    };

    // Toggle active card on mobile
    const toggleCardOptions = (id: string, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent bubbling
        if (isMobile) {
            setActiveCard(activeCard === id ? null : id);
        }
    };

    // Close card options when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (activeCard) {
                setActiveCard(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [activeCard]);

    // Click outside handler for menus
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (sortMenuRef.current && !sortMenuRef.current.contains(event.target as Node)) {
                setIsSortMenuOpen(false);
            }

            if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
                setIsFilterMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter and sort cards
    const filteredCards = details.filter((detail: CardDetail) => {
        // Apply search filter
        if (searchQuery) {
            const searchLower = searchQuery.toLowerCase();
            if (!(
                detail.name?.toLowerCase().includes(searchLower) ||
                detail.encrypted_card_holder_name?.toLowerCase().includes(searchLower) ||
                detail.encrypted_card_number?.toLowerCase().includes(searchLower) ||
                detail.encrypted_expiration_date?.toLowerCase().includes(searchLower)
            )) {
                return false;
            }
        }

        // Apply shared filter
        if (showSharedOnly && detail.is_owner) {
            return false;
        }

        return true;
    });

    // Sort cards
    const sortedCards = [...filteredCards].sort((a, b) => {
        switch (sortOption) {
            case 'alphabetical':
                return (a.name || '').localeCompare(b.name || '');
            case 'oldest':
                return new Date(a.updated_at || a.created_at || '').getTime() - new Date(b.updated_at || b.created_at || '').getTime();
            case 'newest':
            default:
                return new Date(b.updated_at || b.created_at || '').getTime() - new Date(a.updated_at || a.created_at || '').getTime();
        }
    });

    // Apply filter settings from temporary state to active state
    const applyFilters = () => {
        setShowSharedOnly(tempShowSharedOnly);
        setIsFilterMenuOpen(false);
    };

    // Reset temporary filter settings
    const resetFilters = () => {
        setShowSharedOnly(false);
        setTempShowSharedOnly(false);
    };

    // Auto-select first card in list view
    useEffect(() => {
        if (viewMode === 'list' && sortedCards.length > 0 && !selectedCard) {
            setSelectedCard(sortedCards[0]);
        }
    }, [viewMode, sortedCards, selectedCard]);

    // Handle copy to clipboard
    const handleCopyItem = (label: string, value: string) => {
        if (value) {
            navigator.clipboard.writeText(value);
            toast.success(`${label} copied to clipboard`);
        }
    };

    // Sort menu component
    const SortMenu = () => {
        return (
            <div className="relative z-50" ref={sortMenuRef}>
                <button
                    onClick={() => {
                        setIsSortMenuOpen(!isSortMenuOpen);
                        setIsFilterMenuOpen(false);
                    }}
                    className="flex items-center px-3 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-sm font-medium text-white hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                    <ArrowDownIcon className="h-4 w-4 mr-1" />
                    Sort
                    <ChevronDownIcon className="h-4 w-4 ml-1" />
                </button>

                {isSortMenuOpen && (
                    <div className="absolute mt-2 left-0 sm:right-0 sm:left-auto w-48 max-w-[90vw] bg-white shadow-lg rounded-md overflow-hidden z-[9999] border border-gray-200">
                        <div className="py-1">
                            {/* Newest First */}
                            <button
                                className={`flex items-center justify-between w-full px-3 py-2 text-left text-sm rounded-md ${sortOption === "newest"
                                        ? "bg-primary-50 text-primary-700"
                                        : "hover:bg-gray-50"
                                    }`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSortOption("newest");
                                    setIsSortMenuOpen(false);
                                }}
                            >
                                <span>Newest First</span>
                                {sortOption === "newest" && <ArrowDownIcon className="h-4 w-4" />}
                            </button>

                            {/* Oldest First */}
                            <button
                                className={`flex items-center justify-between w-full px-3 py-2 text-left text-sm rounded-md ${sortOption === "oldest"
                                        ? "bg-primary-50 text-primary-700"
                                        : "hover:bg-gray-50"
                                    }`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSortOption("oldest");
                                    setIsSortMenuOpen(false);
                                }}
                            >
                                <span>Oldest First</span>
                                {sortOption === "oldest" && <ArrowDownIcon className="h-4 w-4" />}
                            </button>

                            {/* Alphabetical */}
                            <button
                                className={`flex items-center justify-between w-full px-3 py-2 text-left text-sm rounded-md ${sortOption === "alphabetical"
                                        ? "bg-primary-50 text-primary-700"
                                        : "hover:bg-gray-50"
                                    }`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSortOption("alphabetical");
                                    setIsSortMenuOpen(false);
                                }}
                            >
                                <span>Alphabetical</span>
                                {sortOption === "alphabetical" && (
                                    <ArrowDownIcon className="h-4 w-4" />
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );

    };

    // Filter menu component
    const FilterMenu = () => {
        return (
            <div className="relative z-50" ref={filterMenuRef}>
                <button
                    onClick={() => {
                        setIsFilterMenuOpen(!isFilterMenuOpen);
                        setIsSortMenuOpen(false);
                        setTempShowSharedOnly(showSharedOnly);
                    }}
                    className="flex items-center px-3 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-sm font-medium text-white hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                    <FunnelIcon className="h-4 w-4 mr-1" />
                    Filter
                    {showSharedOnly && (
                        <span className="ml-1 px-1.5 py-0.5 bg-white/30 text-white text-xs rounded-full">
                            1
                        </span>
                    )}
                    <ChevronDownIcon className="h-4 w-4 ml-1" />
                </button>

                {isFilterMenuOpen && (
                    <div className="absolute mt-2 right-0 w-64 bg-white shadow-lg rounded-md overflow-hidden z-[9999] border border-gray-200">
                        <div className="flex justify-between items-center p-3 border-b border-gray-200">
                            <span className="font-medium">Filter cards</span>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    resetFilters();
                                }}
                                className="text-sm text-primary-600 hover:text-primary-800"
                            >
                                Reset
                            </button>
                        </div>

                        {/* Filter Options */}
                        <div className="p-3">
                            <div className="space-y-2">
                                <div className="flex items-center">
                                    <input
                                        id="sharedOnly"
                                        type="checkbox"
                                        checked={tempShowSharedOnly}
                                        onChange={(e) => setTempShowSharedOnly(e.target.checked)}
                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="sharedOnly" className="ml-2 block text-sm text-gray-700">
                                        Shared with me only
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="p-3 border-t border-gray-200">
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    applyFilters();
                                }}
                                className="w-full py-2 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header Controls */}
            <div className="sticky top-0 z-40 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 backdrop-blur-sm border-b border-primary-300 dark:border-primary-700 px-4 sm:px-6 py-6 shadow-lg">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-64 h-32 bg-white rounded-full -translate-y-16 translate-x-32"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-24 bg-white rounded-full translate-y-12 -translate-x-24"></div>
                </div>

                <div className="relative flex flex-col gap-6 sm:flex-row sm:flex-wrap sm:items-center lg:justify-between">
                    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4 sm:gap-6 mb-4 sm:mb-0">
                        {/* Title and Icon for Desktop */}
                        <div className="hidden sm:flex items-center space-x-3">
                            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30 shadow-lg">
                                <CreditCardIcon className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-white">Card Details</h1>
                                <p className="text-white/70 text-sm">Manage your card information</p>
                            </div>
                        </div>

                        {/* Mobile Header */}
                        <div className="sm:hidden">
                            <div className="flex items-center space-x-3 mb-3">
                                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 shadow-lg">
                                    <CreditCardIcon className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-white">Card Details</h1>
                                    <p className="text-white/70 text-xs">Manage your card information</p>
                                </div>
                            </div>
                        </div>

                        {/* Count Display */}
                        <div className="flex items-center space-x-3 px-3 sm:px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl border border-white/30 flex-shrink-0 min-w-[160px]">
                            <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg">
                                <CreditCardIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            </div>
                            <div>
                                <div className="text-lg sm:text-2xl font-bold text-white">
                                    {sortedCards.length}
                                </div>
                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                    {sortedCards.length === 1 ? 'Card' : 'Cards'}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto sm:justify-end">
                        {/* Sort Menu */}
                        <div className="flex-1 min-w-[180px] sm:flex-none sm:min-w-0">
                            <SortMenu />
                        </div>

                        {/* Filter Menu */}
                        <div className="flex-1 min-w-[180px] sm:flex-none sm:min-w-0">
                            <FilterMenu />
                        </div>

                        {/* View Toggle */}
                        <div className="hidden sm:flex items-center bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl p-1 shadow-sm border border-white/30 flex-shrink-0">
                            <button
                                onClick={() => handleViewModeChange('grid')}
                                className={`p-1.5 sm:p-2.5 rounded-lg transition-all duration-200 ${viewMode === 'grid'
                                    ? 'bg-white text-primary-600 shadow-md scale-105'
                                    : 'text-white/80 hover:text-white hover:bg-white/20'
                                    }`}
                                title="Grid View"
                            >
                                <Squares2X2Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                            <button
                                onClick={() => handleViewModeChange('list')}
                                className={`p-1.5 sm:p-2.5 rounded-lg transition-all duration-200 ${viewMode === 'list'
                                    ? 'bg-white text-primary-600 shadow-md scale-105'
                                    : 'text-white/80 hover:text-white hover:bg-white/20'
                                    }`}
                                title="List View"
                            >
                                <ListBulletIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {sortedCards.length === 0 && (
                <div className="text-center py-16 px-6">
                    <div className="mx-auto w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 
                              rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                        <CreditCardIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-2">No cards found</h3>
                    <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                        {searchQuery || showSharedOnly
                            ? "No cards match your search criteria. Try adjusting your search terms or filters."
                            : "Get started by adding your first card to securely manage your payment information."
                        }
                    </p>
                </div>
            )}

            {sortedCards.length > 0 && (
                <>
                    {/* Main Content */}
                    <div className="p-6">
                        {viewMode === 'grid' ? (
                            /* Grid View */
                            <div className="card-details-section grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 p-4 sm:p-6">
                                {sortedCards.map((detail) => (
                                    <div
                                        key={detail.id}
                                        className="relative h-52 rounded-xl shadow-lg overflow-hidden"
                                        onMouseEnter={() => !isMobile && setHoveredCard(detail.id)}
                                        onMouseLeave={() => !isMobile && setHoveredCard(null)}
                                        onClick={(e) => isMobile && toggleCardOptions(detail.id, e)}
                                    >
                                        {/* Front of Card */}
                                        <div className={`absolute inset-0 w-full h-full bg-gradient-to-br from-[#4364F7] to-[#6FB1FC] p-6 transition-all duration-300 ${hoveredCard === detail.id && !isMobile ? 'opacity-0' : 'opacity-100'}`}>
                                            {/* Top right section for favorite icon and mobile menu */}
                                            <div className="absolute top-2 right-2 flex items-center">
                                                {detail.favourite && !hideFavoriteIcon && (
                                                    <StarIconSolid className="h-6 w-6 text-yellow-400 mr-2" />
                                                )}
                                                {/* Mobile menu icon */}
                                                {isMobile && (
                                                    <button
                                                        onClick={(e) => toggleCardOptions(detail.id, e)}
                                                        className="text-white p-1 rounded-full hover:bg-white/10 focus:outline-none"
                                                    >
                                                        <EllipsisVerticalIcon className="h-5 w-5" />
                                                    </button>
                                                )}
                                            </div>

                                            {/* Top Section - Card Name & Chip */}
                                            <div className="flex justify-between items-start mb-6 mt-3">
                                                <h3 className="text-xl font-bold text-white">
                                                    {detail.name || "Unnamed Card"}
                                                </h3>

                                                {/* Card Chip */}
                                                <div className="w-12 h-10 bg-white/20 rounded-md shadow-sm flex items-center justify-center overflow-hidden">
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6 text-white">
                                                        <g fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                                                            <path d="M16.2,16.2c2.5-2.5,2.5-6.6,0-9.2" />
                                                            <path d="M19,19c3.8-3.8,3.8-10.2,0-14" />
                                                            <path d="M13.4,13.4c1.2-1.2,1.2-3.1,0-4.2" />
                                                            <circle cx="9.5" cy="11.3" r="1.5" fill="currentColor" stroke="none" />
                                                        </g>
                                                    </svg>
                                                </div>
                                            </div>

                                            {/* Card Holder & Expiry */}
                                            <div className="flex justify-between items-end">
                                                <div>
                                                    <p className="text-xs text-white/70 uppercase tracking-wider mb-1">Card Holder</p>
                                                    <p className="text-sm text-white font-medium tracking-wide">
                                                        {detail.encrypted_card_holder_name || "CARD HOLDER"}
                                                    </p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-xs text-white/70 uppercase tracking-wider mb-1">Valid Thru</p>
                                                    <p className="text-sm text-white font-medium">
                                                        {detail.encrypted_expiration_date || "MM/YY"}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Footer - Account Number & Card Network */}
                                            <div className="absolute bottom-6 left-6 flex justify-between w-full pr-10">
                                                <p className="text-lg tracking-wider mb-2 text-white">
                                                    **** **** **** {detail.encrypted_card_number?.slice(-4) || '****'}
                                                </p>
                                                <div className="flex space-x-1">
                                                    <div className="w-8 h-8 rounded-full bg-white/30" />
                                                    <div className="w-8 h-8 rounded-full bg-white/20" />
                                                </div>
                                            </div>

                                            {/* Display Categories */}
                                            <div className="absolute bottom-0 left-0 right-0 px-4 py-3 mt-2 flex justify-between items-center">
                                                <div className="flex items-center space-x-2">
                                                    {detail.categories && detail.categories.length > 0 && (
                                                        renderCategories(detail.categories, categories)
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Desktop hover controls */}
                                        {!isMobile && (
                                            <>
                                            
                                                {(isAdminView || (!detail.is_owner && detail.permission_level && detail.permission_level === 'read' && role_id !== 1 && role_id !== 2)) ? (
                                                    <div className={`absolute inset-0 w-full h-full bg-gradient-to-br from-[#4364F7] to-[#6FB1FC] flex flex-col items-center justify-center transition-all duration-300 ${hoveredCard === detail.id ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                                                        <button
                                                            onClick={() => handleEdit(detail.id, detail.is_shared, detail.shared_key)}
                                                            className="w-3/4 py-2 px-4 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
                                                        >
                                                            <EyeIcon className="h-5 w-5" />
                                                            <span>View</span>
                                                        </button>
                                                        {isAdminView && (
                                                            <button
                                                                onClick={() => handleClone(detail)}
                                                                className="w-3/4 py-2 px-4 bg-green-500/20 hover:bg-green-500/30 text-white rounded-lg transition-colors flex items-center justify-center space-x-2 mt-3"
                                                            >
                                                                <DocumentDuplicateIcon className="h-5 w-5" />
                                                                <span>Clone</span>
                                                            </button>
                                                        )}

                                                    </div>)
                                                    :
                                                    <div className={`absolute inset-0 w-full h-full bg-gradient-to-br from-[#4364F7] to-[#6FB1FC] flex flex-col items-center justify-center transition-all duration-300 ${hoveredCard === detail.id ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                                                        <button
                                                            onClick={() => handleEdit(detail.id, detail.is_shared, detail.shared_key)}
                                                            className="w-3/4 py-2 px-4 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
                                                        >
                                                            <PencilIcon className="h-5 w-5" />
                                                            <span>Edit</span>
                                                        </button>
                                                        {((detail.is_owner) || (!detail.is_owner && role_id !== 3)) && (
                                                            <button
                                                                onClick={() => handleDeleteClick(detail.id, detail.is_owner ?? false)}
                                                                className="w-3/4 py-2 px-4 bg-red-500/20 hover:bg-red-500/30 text-white rounded-lg transition-colors flex items-center justify-center space-x-2 mt-3"
                                                            >
                                                                <TrashIcon className="h-5 w-5" />
                                                                <span>Delete</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                }
                                            </>
                                        )}

                                        {/* Mobile action menu */}
                                        {isMobile && activeCard === detail.id && (
                                            <div
                                                className="absolute right-0 left-0 bottom-0 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-b-lg z-10 p-3 shadow-lg"
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ borderTop: '1px solid rgba(255,255,255,0.2)' }}
                                            >
                                                <div className="flex justify-between items-center mb-2">
                                                    <h4 className="font-medium text-gray-800 dark:text-white">Card options</h4>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setActiveCard(null);
                                                        }}
                                                        className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    >
                                                        <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                                                    </button>
                                                </div>
                                                {( isAdminView ||(!detail.is_owner && detail.permission_level && detail.permission_level === 'read' && role_id !== 1 && role_id !== 2)) ?
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <button
                                                            onClick={() => handleEdit(detail.id, detail.is_shared, detail.shared_key)}
                                                            className="py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center justify-center space-x-1"
                                                        >
                                                            <EyeIcon className="h-4 w-4" />
                                                            <span>View</span>
                                                        </button>
                                                        {isAdminView && (
                                                            <button
                                                                onClick={() => handleClone(detail)}
                                                                className="py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center justify-center space-x-1"
                                                            >
                                                                <DocumentDuplicateIcon className="h-4 w-4" />
                                                                <span>Clone</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                    :
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <button
                                                            onClick={() => handleEdit(detail.id, detail.is_shared, detail.shared_key)}
                                                            className="py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center justify-center space-x-1"
                                                        >
                                                            <PencilIcon className="h-4 w-4" />
                                                            <span>Edit</span>
                                                        </button>
                                                        {((detail.is_owner) || (!detail.is_owner && role_id !== 3)) && (
                                                            <button
                                                                onClick={() => handleDeleteClick(detail.id, detail.is_owner ?? false)}
                                                                className="py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center justify-center space-x-1"
                                                            >
                                                                <TrashIcon className="h-4 w-4" />
                                                                <span>Delete</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                }
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            /* List View with Sidebar */
                            <div className="flex h-[calc(100vh-180px)] bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                                {/* Left Panel - Card List */}
                                <div className="w-1/2 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
                                    <div className="p-4 space-y-3">
                                        {sortedCards.map((detail) => (
                                            <div
                                                key={detail.id}
                                                className="relative group"
                                                onMouseEnter={() => setHoveredCard(detail.id)}
                                                onMouseLeave={() => setHoveredCard(null)}
                                            >
                                                <div
                                                    className={`relative p-6 bg-white dark:bg-gray-800 rounded-2xl border-2 transition-all duration-300 cursor-pointer overflow-hidden
                                                ${selectedCard?.id === detail.id
                                                            ? 'border-primary-300 dark:border-primary-600 bg-primary-50/50 dark:bg-primary-900/20 shadow-lg shadow-primary-100 dark:shadow-primary-900/30'
                                                            : 'border-gray-200 dark:border-gray-700 hover:border-primary-200 dark:hover:border-primary-700 hover:shadow-lg dark:hover:shadow-gray-900/50'
                                                        }`}
                                                    onClick={() => setSelectedCard(detail)}
                                                >
                                                    <div className="flex items-center space-x-5">
                                                        {/* Card Icon */}
                                                        <div className="w-16 h-10 rounded-xl bg-gradient-to-br from-[#4364F7] to-[#6FB1FC] 
                                                              flex items-center justify-center shadow-lg border border-blue-300 flex-shrink-0 group-hover:shadow-xl transition-shadow duration-300">
                                                            <CreditCardIcon className="w-8 h-8 text-white" />
                                                        </div>

                                                        {/* Content */}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center space-x-3 mb-2">
                                                                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                                                    {detail.name || "Unnamed Card"}
                                                                </h3>
                                                                {detail.favourite && !hideFavoriteIcon && (
                                                                    <div className="flex-shrink-0 p-1 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
                                                                        <StarIconSolid className="w-4 h-4 text-yellow-500" />
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="flex items-center space-x-6">
                                                                <div className="flex items-center space-x-2">
                                                                    <div className="p-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                                                        <UserIcon className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                                                    </div>
                                                                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300 truncate max-w-40">
                                                                        {detail.encrypted_card_holder_name || "No holder name"}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center space-x-2">
                                                                    <span className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                                                                        •••• {detail.encrypted_card_number?.slice(-4) || '••••'}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {/* Categories */}
                                                            {detail.categories && detail.categories.length > 0 && (
                                                                <div className="mt-3">
                                                                    {renderCategories(detail.categories, categories)}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Selection indicator */}
                                                        <div className={`w-1 h-12 rounded-full transition-all duration-300 ${selectedCard?.id === detail.id ? 'bg-primary-500' : 'bg-transparent'
                                                            }`} />
                                                    </div>

                                                    {/* Hover effect overlay */}
                                                    <div className={`absolute inset-0 bg-gradient-to-r from-primary-500/5 to-primary-600/5 rounded-2xl transition-opacity duration-300 pointer-events-none ${hoveredCard === detail.id ? 'opacity-100' : 'opacity-0'
                                                        }`} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Right Panel - Card Details */}
                                <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
                                    {selectedCard ? (
                                        <div className="h-full p-6 overflow-y-auto">
                                            {/* Header */}
                                            <div className="flex items-center space-x-4 mb-8 pb-6 border-b border-gray-200 dark:border-gray-700">
                                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#4364F7] to-[#6FB1FC] 
                                                      flex items-center justify-center shadow-lg border border-blue-300">
                                                    <CreditCardIcon className="w-10 h-10 text-white" />
                                                </div>
                                                <div className="flex-1">
                                                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                                                        {selectedCard.name || "Unnamed Card"}
                                                    </h2>
                                                    {selectedCard.favourite && !hideFavoriteIcon && (
                                                        <div className="flex items-center space-x-1 px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                                                            <StarIconSolid className="w-4 h-4 text-yellow-500" />
                                                            <span className="text-xs font-medium text-yellow-700 dark:text-yellow-400">Favorite</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Details */}
                                            <div className="space-y-6">
                                                {/* Card Number */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Card Number
                                                    </label>
                                                    <div className="flex items-center space-x-2">
                                                        <div className="flex-1 flex items-center px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl">
                                                            <CreditCardIcon className="w-5 h-5 text-gray-400 mr-3" />
                                                            <input
                                                                type="text"
                                                                readOnly
                                                                value={selectedCard.encrypted_card_number ? `•••• •••• •••• ${selectedCard.encrypted_card_number.slice(-4)}` : ''}
                                                                className="flex-1 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none font-mono"
                                                            />
                                                        </div>
                                                        <button
                                                            onClick={() => handleCopyItem('Card number', selectedCard.encrypted_card_number || '')}
                                                            className="p-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl transition-colors shadow-lg hover:shadow-xl"
                                                            title="Copy card number"
                                                        >
                                                            <ClipboardDocumentIcon className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Card Holder */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Card Holder Name
                                                    </label>
                                                    <div className="flex items-center space-x-2">
                                                        <div className="flex-1 flex items-center px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl">
                                                            <UserIcon className="w-5 h-5 text-gray-400 mr-3" />
                                                            <input
                                                                type="text"
                                                                readOnly
                                                                value={selectedCard.encrypted_card_holder_name || ''}
                                                                className="flex-1 bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none"
                                                            />
                                                        </div>
                                                        <button
                                                            onClick={() => handleCopyItem('Card holder name', selectedCard.encrypted_card_holder_name || '')}
                                                            className="p-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl transition-colors shadow-lg hover:shadow-xl"
                                                            title="Copy card holder name"
                                                        >
                                                            <ClipboardDocumentIcon className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Categories */}
                                                {selectedCard.categories && selectedCard.categories.length > 0 && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                            Vaults
                                                        </label>
                                                        {renderCategories(selectedCard.categories, categories)}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                                                <div className="flex justify-center space-x-4">
                                                    {(isAdminView || (!selectedCard?.is_owner && selectedCard.permission_level === 'read' && role_id !== 1 && role_id !== 2)) ? (
                                                        <button
                                                            onClick={() => handleEdit(selectedCard.id, selectedCard.is_shared, selectedCard.shared_key)}
                                                            className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl transition-colors flex items-center justify-center shadow-lg hover:shadow-xl min-w-[120px]"
                                                        >
                                                            <EyeIcon className="h-4 w-4 mr-2" />
                                                            View
                                                        </button>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() => handleEdit(selectedCard.id, selectedCard.is_shared, selectedCard.shared_key)}
                                                                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl transition-colors flex items-center justify-center shadow-lg hover:shadow-xl min-w-[120px]"
                                                            >
                                                                <PencilIcon className="h-4 w-4 mr-2" />
                                                                Edit
                                                            </button>
                                                            {((selectedCard?.is_owner) || (!selectedCard?.is_owner && role_id !== 3)) && (
                                                                <button
                                                                    onClick={() => handleDeleteClick(selectedCard.id, selectedCard.is_owner ?? false)}
                                                                    className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors flex items-center justify-center shadow-lg hover:shadow-xl min-w-[120px]"
                                                                >
                                                                    <TrashIcon className="h-4 w-4 mr-2" />
                                                                    Delete
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        // Default state
                                        <div className="h-full flex items-center justify-center p-6">
                                            <div className="text-center">
                                                <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 
                                                      rounded-2xl flex items-center justify-center mb-6 shadow-lg mx-auto">
                                                    <CreditCardIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                                                </div>
                                                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-2">Select a Card</h3>
                                                <p className="text-gray-500 dark:text-gray-400">
                                                    Choose a card from the list to view details and manage information
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Confirm Modal */}
            <ConfirmModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Card"
                message="Are you sure you want to delete this card? This action cannot be undone."
                confirmText="Delete"
                isLoading={isDeleting}
            />
        </div>
    );
};

export default CardDetailsList; 